import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, OnInit, signal, viewChild } from '@angular/core';
import { CommonButtonRowWidget, CommonSelectFieldWidget, CommonTextFieldWidget, UtilityService } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { Note, NoteCategoryId } from '@interfaces';
import { AppService } from '../../../../services';
import { NoteEditorForm, NotesEditorFields } from './notes-editor.form';
import { NotesEditorService } from './notes-editor.service';

interface NoteWithFormattedDate extends Note {
	formattedUpdatedDate: string,
	formattedCreatedDate: string,
	icon: string
}

@Component({
	selector: 'notes-editor-part',
	standalone: true,
	imports: [
		NgClass,
		CommonTextFieldWidget,
		CommonSelectFieldWidget,
		CommonButtonRowWidget
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './notes-editor.part.html'
})

export class NotesEditorPart implements OnInit, AfterViewInit {

	app = inject(AppService);
	public noteService = inject(NotesEditorService);
	private dialogService = inject(DialogService);
	private util = inject(UtilityService);

	public readonly noteId = input<number | undefined>(undefined);
	public readonly personId = input<number | undefined>(undefined);
	public readonly accVentureId = input<number | undefined>(undefined);
	public form = new NoteEditorForm();
	public noteToEdit = signal<NoteWithFormattedDate | undefined>(undefined);

	public noteFormSaveBtn = viewChild.required<ElementRef<HTMLButtonElement>>('noteFormSaveBtn');

	ngOnInit() {
		this.form.setValues();
		this.noteService.getNotesData(this.personId(), this.accVentureId());
	}

	ngAfterViewInit() {
		this.form.form.setSubmitButton(
			this.noteFormSaveBtn(),
			async (values: NotesEditorFields, changed: string[]) => {
				const noteId = this.noteToEdit()?.noteId;
				if (noteId)
					await this.noteService.updateNote(noteId, { ...values });
				else
					await this.noteService.addNote({ ...values });
				this.showForm(false);
			}
		);
	}

	notes = computed<NoteWithFormattedDate[]>(() => {
		const notes = this.noteService.notesData();
		return notes.map(n => ({
			...n,
			formattedCreatedDate: this.util.date.formatUTC(n.createdUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US'),
			formattedUpdatedDate: this.util.date.formatUTC(n.updatedUTC, 'MMM D, YYYY', 'H:MM AM EST', 'en-US'),
			icon: n.noteCategoryId === NoteCategoryId.RedFlag ? 'red fa-flag fas fa-fw text-danger' : 'far fa-sticky-note fa-fw'
		}))
	})

	showForm(flag: boolean, noteData?: NoteWithFormattedDate) {
		this.form.setValues(noteData);
		this.form.showForm.set(flag);
		this.noteToEdit.set(noteData ?? undefined);
	}

	setInitialValuesforEdit() {
		this.form.setValues(this.noteToEdit());
	}

	async confirmNoteDeletion(noteId: number) {
		const yes = await this.dialogService.confirm(`Are you sure you want to remove the Note?`, 'Yes', 'No', [], 400, 200);
		if (yes) {
			await this.noteService.deleteNote(noteId);
		}
	}
}